import { Checkbox, FormControlLabel } from "@material-ui/core";
import React from "react";
import Modal from "react-bootstrap/Modal";
import closeIcon from "../../../../assets/icons/close.svg";
import { gql } from "@apollo/client";
import { apolloClient } from "../../../../index";
import { v4 } from "uuid";
import { useParams } from "react-router-dom";
import ReorderColumnsSection from "./ReorderColumnsSection/ReorderColumnsSection";

// Styles
import "./CustomizeColumnModal.scss";

const defaultColumns = ["File Name", "Status", "Added By", "Modified On", "Modified By", "Shared With", "Tag Name"];

const defaultArchiveColumns = ["File Name", "Location", "Archived By", "Archived On", "Archived Till"];

const CustomizeColumnModal = ({
  showModal,
  isDocumentArchive,
  availableColumns,
  selectedColumns,
  handleCancel,
  handleApply,
  columnsDisplayMapping,
  reloadColumns,
  stopReloadColumns,
  columns,
  setColumns,
}) => {
  let params = useParams();
  const [searchValue, setSearchValue] = React.useState("");
  const [appliedColumns, setAppliedColumns] = React.useState([]);
  const [allColumns, setAllColumns] = React.useState([]);

  React.useEffect(() => {
    if (!reloadColumns) {
      return;
    }

    stopReloadColumns();
    if (isDocumentArchive) {
      const variables = {
        folderId: params.subId,
        isArchive: true,
      };
      apolloClient
        .query({
          query: gql`
            query FieldsOfAllDocuments($folderId: UUID!, $isArchive: Boolean!) {
              fieldsOfAllDocuments(folderId: $folderId, isArchive: $isArchive)
            }
          `,
          variables: variables,
        })
        .then((result) => {
          const columns = [];
          result.data.fieldsOfAllDocuments.map((field) => {
            !defaultArchiveColumns.includes(field) && !columns.includes(field) && columns.push(field);
            return true;
          });

          setColumns(columns);
        })
        .catch((e) => {});
    } else {
      const variables = {
        folderId: params.subId,
        isArchive: false,
      };
      apolloClient
        .query({
          query: gql`
            query FieldsOfAllDocuments($folderId: UUID!, $isArchive: Boolean!) {
              fieldsOfAllDocuments(folderId: $folderId, isArchive: $isArchive)
            }
          `,
          variables: variables,
        })
        .then((result) => {
          const columns = [];
          result.data.fieldsOfAllDocuments.map((field) => {
            !defaultColumns.includes(field) && !columns.includes(field) && columns.push(field);
            return true;
          });
          setColumns(columns);
        })
        .catch((e) => {});
    }
  }, [columns, setColumns, isDocumentArchive, params.subId, reloadColumns, stopReloadColumns]);

  React.useEffect(() => {
    const totalColumns = [...availableColumns, ...columns].map((column, index) => {
      const mappedColumn = columnsDisplayMapping.hasOwnProperty(column) ? columnsDisplayMapping[column] : column;
      let selectedIndex = selectedColumns.indexOf(column);
      let selected = true;
      if (selectedIndex === -1) {
        selectedIndex = selectedColumns.length + index;
        selected = false;
      }
      return {
        id: v4(),
        title: mappedColumn,
        originalTitle: column,
        selected: selected,
        index: selectedIndex,
      };
    });
    setAllColumns(totalColumns);
    setAppliedColumns([...totalColumns]);
  }, [columnsDisplayMapping, selectedColumns, columns, availableColumns]);

  const selectedLength = allColumns.filter((column) => column.selected === true).length;

  const handleSelectAll = (e) => {
    setAllColumns((allColumns) =>
      allColumns.map((column) => {
        return { ...column, selected: e.target.checked };
      })
    );
  };

  const handleSelectColumn = (selected, id) => {
    setAllColumns((allColumns) =>
      allColumns.map((column) => {
        if (column.id === id) {
          return { ...column, selected: selected };
        } else {
          return column;
        }
      })
    );
  };

  const resetColumnSelection = () => {
    setAllColumns((allColumns) => {
      return allColumns.map((column, index) => {
        return {
          ...column,
          selected: isDocumentArchive
            ? defaultArchiveColumns.includes(column.title)
            : defaultColumns.includes(column.title),
          index: index,
        };
      });
    });
  };

  const setAllColumnsWithAppliedColumns = () => {
    setAllColumns([...appliedColumns]);
  };

  const handleApplyClick = () => {
    handleApply(
      [...allColumns.filter((column) => column.selected)]
        .sort((column1, column2) => (column1.index > column2.index ? 1 : -1))
        .map(({ originalTitle }) => originalTitle)
    );
    setAppliedColumns([...allColumns]);
    handleCancel();
  };

  const handleCancelClick = () => {
    handleCancel();
    setSearchValue("");
    setAllColumnsWithAppliedColumns();
  };

  const filteredColumns = allColumns.filter(
    ({ title }) => searchValue === "" || title.toLowerCase().includes(searchValue.trim().toLowerCase())
  );

  return (
    <Modal className="customize-column-modal" size="md" show={showModal} onHide={handleCancelClick}>
      <div className="customize-column-modal-content">
        <div className="customize-column-modal-content-header">
          <span className="title">Customize Columns</span>
          <div className="actions">
            <span className="reset-button" onClick={resetColumnSelection}>
              Reset
            </span>
            <img
              height={18}
              width={18}
              className="close-button"
              src={closeIcon}
              alt="close"
              onClick={handleCancelClick}
            />
          </div>
        </div>
        <div className="customize-column-modal-content-body">
          <div className="column-selection-section">
            <div className="column-selection-section-search">
              <div className="search-box mr-2">
                <input
                  type="text"
                  maxLength="50"
                  placeholder="&#xF002;  Search columns"
                  className="search-placeholder set-width"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <FormControlLabel
                  aria-label="Select All"
                  onClick={(event) => event.stopPropagation()}
                  onFocus={(event) => event.stopPropagation()}
                  control={
                    <Checkbox
                      checked={selectedLength === allColumns.length}
                      onChange={handleSelectAll}
                      disableRipple={true}
                      className="accordion-checkbox"
                      size="small"
                      indeterminate={selectedLength !== 0 && selectedLength !== allColumns.length}
                    />
                  }
                  label="Select All"
                />
              </div>
            </div>
            <div className="column-selection-section-list">
              {filteredColumns.length === 0 ? (
                <div className="column-selection-section-list-empty">No columns found !</div>
              ) : (
                filteredColumns.map(({ title, id, selected }) => (
                  <FormControlLabel
                    aria-label="title"
                    onClick={(event) => event.stopPropagation()}
                    onFocus={(event) => event.stopPropagation()}
                    key={id}
                    control={
                      <Checkbox
                        checked={selected}
                        onChange={(e) => handleSelectColumn(e.target.checked, id)}
                        disableRipple
                        className="accordion-checkbox"
                        size="small"
                      />
                    }
                    label={title}
                  />
                ))
              )}
            </div>
          </div>
          <div className="column-sorting-section">
            <span className="column-sorting-section-title">Selected Columns ({selectedLength})</span>
            <div className="column-sorting-section-draggables">
              <ReorderColumnsSection
                {...{
                  allColumns,
                  setAllColumns,
                  handleSelectColumn,
                }}
              />
            </div>
          </div>
        </div>
        <div className="customize-column-modal-content-footer">
          <button type="button" className="btn-secondary" onClick={handleCancelClick}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleApplyClick} disabled={selectedLength === 0}>
            Apply
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CustomizeColumnModal;
